import { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';

import { AuthProvider, AuthRoute, useAuth } from '../global/contexts/auth/auth';
import { queryClient } from '../core/network/query-client';
import PortalShell from './portal-shell';

import LoginPage from '../pages/login';
import RegisterPage from '../pages/register';
import LegalPage from '../pages/legal';
import EventoPage from '../pages/evento';
import EventoPantallaPage from '../pages/evento-pantalla';
import UnauthorizedPage from '../pages/unauthorized';
import HomePage from '../pages/client/home';
import InformesPage from '../pages/client/informes';
import ParceriasPage from '../pages/client/parcerias';
import MisVacasPage from '../pages/client/mis-vacas';
import FincasPage from '../pages/client/fincas';
import DonadorasExternasPage from '../pages/client/donadoras-externas';
import PerfilPage from '../pages/client/perfil';

// TSS vite/01 — app shell. Wires providers, router, layout chrome.
// TSS vite/03 §"Provider composition order" — Router → Auth → QueryClient → cross-app providers.
//
// Cowre · Portal de clientes — canal público (/api/v1/public). Fase 1:
// login de cliente + parcerías (receptoras) read-only. Detalle de receptora
// (+KPIs) e informes corren PROVISIONAL(Q-118) contra contratos asumidos
// (docs/provisional-contracts.md); cowhousing (Mis vacas) va contra contrato
// real (public/donors, public/stats); crías (genetics_buyer) espera contrato.

export default function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <QueryClientProvider client={queryClient}>
                    <AppGate>
                        <AppRouter />
                    </AppGate>
                    <Toaster position="top-right" />
                </QueryClientProvider>
            </AuthProvider>
        </BrowserRouter>
    );
}

// Blocks rendering until the auth bootstrap resolves so guarded routes
// never flash unauthorized content during the initial public/me call.
function AppGate({ children }) {
    const { initializing } = useAuth();
    if (initializing) {
        return (
            <div className="boot-loader">
                <div className="boot-loader__spinner" />
                <span className="boot-loader__text">Verificando sesión...</span>
            </div>
        );
    }
    return children;
}

// /logout must actually END the session (cookies + state + query cache), not
// just redirect — a bare <Navigate> left the httpOnly cookie alive. Executes
// logout() once (idempotent, crash-safe) and then lands on /login.
function LogoutRoute() {
    const { logout } = useAuth();
    const [done, setDone] = useState(false);

    useEffect(() => {
        let cancelled = false;
        logout().finally(() => { if (!cancelled) setDone(true); });
        return () => { cancelled = true; };
    }, [logout]);

    if (!done) return null;
    return <Navigate to="/login?logout=true" replace />;
}

function AppRouter() {
    return (
        <Routes>
            {/* Pre-auth surfaces — no shell (full-screen). */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register/:token" element={<RegisterPage />} />
            <Route path="/legal" element={<LegalPage />} />
            <Route path="/evento/:token" element={<EventoPage />} />
            <Route path="/evento/:token/pantalla" element={<EventoPantallaPage />} />
            <Route path="/unauthorized" element={<UnauthorizedPage />} />
            <Route path="/logout" element={<LogoutRoute />} />

            {/* Authenticated client surfaces — rendered inside the portal shell
                (sidebar + mobile nav + footer). Ownership is enforced server-side
                (scope by token client_id), so AuthRoute is the front gate. */}
            <Route element={<AuthRoute><PortalShell /></AuthRoute>}>
                <Route path="/" element={<HomePage />} />
                <Route path="/parcerias" element={<ParceriasPage />} />
                <Route path="/parcerias/:recipientId" element={<ParceriasPage />} />
                <Route path="/informes" element={<InformesPage />} />
                <Route path="/mis-vacas" element={<MisVacasPage />} />
                <Route path="/mis-vacas/:donorId" element={<MisVacasPage />} />
                <Route path="/fincas" element={<FincasPage />} />
                <Route path="/mis-propuestas" element={<DonadorasExternasPage />} />
                <Route path="/perfil" element={<PerfilPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
        </Routes>
    );
}
